/* eslint-disable react/jsx-no-undef */
import { Box, Link, Typography, useMediaQuery } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { LastBillsTableRow } from "../../core/models/lastBills/lastBills.model";
import {
  downloadBill,
  getBills,
} from "../../core/services/bills/bills.service";
import { GetBillsRequestBody } from "../../core/models/bills/bills.model";
import { formatLastBills } from "../../utils/components/lastBills/lastBills.util";
import "./LastBills.scss";
import LastBillsTable from "./LastBillsTable/LastBillsTable";
import LastBillsCard from "./LastBillsCard/LastBillsCard";
import { useTranslation } from "react-i18next";
import { FormattedOwnerData } from "../../core/models/ownerData/ownerData.model";
import { formatDateString } from "../../utils/dateUtils/formatDate.util";
import { useNavigate } from "react-router-dom";
import TotalEnergiesContext from "../../core/store/context/TotalEnergiesContext";
import LoadingComponentComponent from "../UI/LoadingComponentComponent/LoadingComponentComponent";
import EmptyStateComponent from "../UI/EmptyStateComponent/EmptyStateComponent";
import SnackbarComponent from "../UI/SnackbarComponent/SnackbarComponent";

interface LastBillsProps {
  seeAllButtonText: string;
  idCups?: string;
  cups?: string;
  cif?: string;
  socialReason?: string | null;
  cupsContractId?: number;
}

const LastBills = ({
  seeAllButtonText,
  idCups,
  cups,
  cif,
  socialReason,
  cupsContractId,
}: LastBillsProps) => {
  const { t } = useTranslation();
  const { ownerData, userData, snackbarData, setSnackbarData } =
    useContext(TotalEnergiesContext);
  const navigate = useNavigate();
  const isDesktopViewport = useMediaQuery("(min-width:744px)");
  const [lastBillsTableRows, setLastBillsTableRows] = useState<
    LastBillsTableRow[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);

  const getLastBillsTableRows = useCallback(
    async (
      { isAdministrator, idClient, idAdministrator }: FormattedOwnerData,
      token: string,
      lastBillsAbortController: AbortController,
    ) => {
      const dateTo = new Date();
      // const dateFrom = new Date(new Date().setMonth(dateTo.getMonth() - 3));
      const dateFrom = new Date(
        new Date().setFullYear(dateTo.getFullYear() - 30),
      );
      const isDetailCupsPage = !!cups;

      const getBillRequestBody: GetBillsRequestBody = {
        userid: isAdministrator ? idAdministrator : idClient,
        isAdmin: isAdministrator,
        isLastBillComponent: false,
        dateFrom: formatDateString(dateFrom).replace(",", ""),
        dateTo: formatDateString(dateTo).replace(",", ""),
        ...(cups && { cups: [cups] }),
        ...(cif && { cif: [cif] }),
        pageIndex: "0",
        pageSize: "3",
        contractId: cupsContractId,
        businessName: socialReason ? [socialReason] : [],
        orderBy: {
          FechaFactura: "desc",
        },
        maxBillImport: null,
        minBillImport: null,
      };

      getBills(getBillRequestBody, token, lastBillsAbortController.signal)
        .then(({ bills }) => {
          setIsLoading(false);
          setLastBillsTableRows(formatLastBills(bills, isAdministrator));
        })
        .catch((error) => {
          setIsLoading(false);
        });
    },
    [cif, cups, cupsContractId, socialReason],
  );

  useEffect(() => {
    const lastBillsAbortController = new AbortController();

    if (ownerData && userData) {
      setIsLoading(true);
      getLastBillsTableRows(
        ownerData,
        userData.token,
        lastBillsAbortController,
      );

      return () => {
        lastBillsAbortController.abort();
      };
    }
  }, [getLastBillsTableRows, ownerData, userData]);

  const handleClickDonwloadBill = async (id: string): Promise<void> => {
    if (userData) {
      setIsDownloadLoading(true);
      await downloadBill(id, userData.token)
        .then(() => {
          setSnackbarData({
            isOpen: true,
            isSuccessSeverity: true,
            text: "Descarga realizada con éxito",
          });
          setIsDownloadLoading(false);
        })
        .catch((error) => {
          setSnackbarData({
            isOpen: true,
            isSuccessSeverity: false,
            text: "Ha habido un error con la descarga",
          });
          setIsDownloadLoading(false);
        });
    }
  };

  const showNoResults = !isLoading && !!!lastBillsTableRows.length;

  return (
    <Box
      component="section"
      className="last-bills"
      sx={{ paddingBottom: "2.5rem", paddingTop: { xs: 10, md: 5 } }}
    >
      <Typography component="h3" variant="h3" sx={{ mb: { md: 4, xs: 3 } }}>
        {t("Últimas facturas.Últimas facturas")}
      </Typography>
      {isLoading && (
        <LoadingComponentComponent
          isLoading={isLoading}
          backgroundColor={"transparent"}
          position={"relative"}
          height={"4rem"}
        />
      )}
      {!!lastBillsTableRows.length && (
        <>
          <Box component="div">
            {isDesktopViewport ? (
              <LastBillsTable
                lastBillsTableRows={lastBillsTableRows}
                handleClickDonwloadBill={handleClickDonwloadBill}
                isDownloadLoading={isDownloadLoading}
              />
            ) : (
              <LastBillsCard
                lastBillsTableRows={lastBillsTableRows}
                handleClickDonwloadBill={handleClickDonwloadBill}
                isDownloadLoading={isDownloadLoading}
              />
            )}
          </Box>
          <Link
            onClick={() =>
              idCups
                ? navigate(
                    `${process.env.REACT_APP_BILLS!}/${idCups}?origin=supplies`,
                  )
                : navigate(process.env.REACT_APP_BILLS!)
            }
            underline="none"
            component="button"
            variant="button"
            sx={{ paddingY: "14px", display: idCups ? "" : "none" }}
          >
            {t(`Últimas facturas.${seeAllButtonText}`)}{" "}
            <Typography
              component="span"
              aria-hidden="true"
              className="t-icon-long-arrow-right"
              sx={{ verticalAlign: "middle", paddingLeft: "10px" }}
            />
          </Link>
          <SnackbarComponent
            snackbarData={snackbarData}
            setSnackbarData={setSnackbarData}
          />
        </>
      )}{" "}
      {showNoResults && (
        <EmptyStateComponent text="No hay resultados" height="17rem" />
      )}
    </Box>
  );
};

export default LastBills;
